'use strict';

import { dwService } from 'amb/services/dw.service';
import { sharedStorage } from '../utils/sharedStorage';
import { tabIdentity } from '../utils/tabIdentity';

/**
 * Cross-tab CSRF stale notification and recovery.
 *
 * **Purpose:** When one tab learns the session CSRF is stale (or fails an AJAX
 * call with `csrfError`), warn peer tabs and recover this tab per Site Preferences
 * — without coordinating basket/checkout ownership.
 *
 * **Scope:**
 * - Publish `csrf-stale` to peers (`notifyCsrfStale`)
 * - Peer filter + action (`onCsrfStale` → modal / reload / none)
 * - Self-tab recovery after AJAX CSRF failure (`handleCsrfFailSelf`)
 * - Bootstrap modal mount / fallback reload (`showCsrfStaleModal`,
 *   `reloadForCsrfRecovery`)
 *
 * **Out of scope:** Token refresh / form field rewriting; Save Execution Gate;
 * tab presence / tab reconcile; tab identify/blink. Bus subscribe for
 * `csrf-stale` is owned by `ajaxHandlers.registerCsrfStaleListener`.
 */
export const csrfPeerRecovery = {
  /**
   * @readonly
   * @private
   */
  STATE_KEY: 'csrf',

  /**
   * @readonly
   * @private
   */
  CSRF_STALE_MODAL_ID: 'csrf-stale-modal',

  /**
   * @private
   *
   * @returns {{ initialized: boolean, csrfStaleModalShown: boolean }}
   */
  get state() {
    return sharedStorage.get(this.STATE_KEY, () => ({
      initialized: false,
      csrfStaleModalShown: false
    }));
  },

  /**
   * Whether the `csrf-stale` peer listener has been registered.
   *
   * @returns {boolean}
   */
  isInitialized() {
    return !!this.state.initialized;
  },

  /**
   * Mark the `csrf-stale` peer listener as registered (idempotent wirer guard).
   */
  setInitialized() {
    this.state.initialized = true;
  },

  /**
   * Tell peer tabs their CSRF may be stale. Does not reload this tab.
   * No-op when Site Preference `csrfStalePeerFilter` is `none`.
   */
  notifyCsrfStale() {
    if (dwService.preference('csrfStalePeerFilter', 'auto') === 'none') {
      return;
    }

    sharedStorage.tabChannel.publish('csrf-stale');
  },

  /**
   * Self-tab recovery after AJAX `csrfError` (`csrfFailSelfAction`).
   */
  handleCsrfFailSelf() {
    this.applyCsrfRecovery(dwService.preference('csrfFailSelfAction', 'reload'));
  },

  /**
   * Peer handler for `csrf-stale` (filter + peer action from Site Preferences).
   *
   * @param {Object} [payload] unused; topic carries the signal
   */
  onCsrfStale(payload = {}) {
    const filter = dwService.preference('csrfStalePeerFilter', 'auto');
    const hasCsrfInput = !!document.querySelector(
      'input[name="csrf_token"], input[name*="csrf_token"]'
    );

    if (filter === 'none' || (filter === 'auto' && !hasCsrfInput)) {
      return;
    }

    this.applyCsrfRecovery(dwService.preference('csrfStalePeerAction', 'modal'));
  },

  /**
   * @private
   *
   * @param {string} action reload | modal | none
   */
  applyCsrfRecovery(action) {
    if (action === 'none') {
      return;
    }

    if (action === 'modal') {
      this.showCsrfStaleModal();

      return;
    }

    this.reloadForCsrfRecovery();
  },

  /**
   * Show page-mounted Bootstrap modal (`#csrf-stale-modal-root`); any hide
   * (and missing / invalid markup) uses reloadForCsrfRecovery. Hides other
   * open modals first so the CSRF dialog is not stacked under them.
   *
   * @private
   */
  showCsrfStaleModal() {
    if (this.state.csrfStaleModalShown) {
      return;
    }

    const $modal = this.findCsrfStaleModal();

    if (!$modal || !$modal.length) {
      this.reloadForCsrfRecovery();

      return;
    }

    this.state.csrfStaleModalShown = true;
    this.disableControlsOutsideCsrfStaleModal($modal);

    this.hideOpenModals($('.modal.show').not($modal)).then(() => {
      $modal.one('hidden.bs.modal', () => this.reloadForCsrfRecovery());
      $modal.modal({ backdrop: 'static', keyboard: false });
    });
  },

  /**
   * Hide the given open Bootstrap modals and resolve when all have finished
   * hiding. Does not dispose or remove page-mounted modals.
   *
   * @private
   *
   * @param {JQuery} $modals
   *
   * @returns {Promise<void[]>}
   */
  hideOpenModals($modals) {
    if (!$modals || !$modals.length) {
      return Promise.resolve([]);
    }

    const hides = $modals.toArray().map((el) => new Promise((resolve) => {
      $(el)
        .one('hidden.bs.modal', resolve)
        .modal('hide');
    }));

    return Promise.all(hides);
  },

  /**
   * Reload after CSRF recovery. Checkout resets to Begin so a deep stage URL
   * is not re-opened with a stale stage; under tab isolation, attach Client Tab
   * Id. Other pages plain-reload. Does not mint/attach mcTab when tab isolation is off.
   *
   * @private
   */
  reloadForCsrfRecovery() {
    const isCheckoutPage = document.querySelector('#checkout-main') !== null;

    if (isCheckoutPage) {
      const url = new URL(dwService.url('Checkout-Begin'));

      if (tabIdentity.isTabIsolation()) {
        const tabId = tabIdentity.peekTabId();

        if (tabId) {
          url.searchParams.set('mcTab', tabId);
        }
      }

      return window.location.replace(url);
    }

    window.location.reload();
  },

  /**
   * @private
   *
   * @returns {JQuery|null}
   */
  findCsrfStaleModal() {
    const $root = $('#csrf-stale-modal-root');

    if (!$root.length) {
      return null;
    }

    let $modal = $root.find('.modal').first();

    if (!$modal.length) {
      $modal = $root.find('#' + this.CSRF_STALE_MODAL_ID);
    }

    return $modal.length ? $modal : null;
  },

  /**
   * Disable visible controls outside the CSRF modal so a fast click cannot
   * submit while the modal is opening. Page reload on hide restores controls.
   *
   * @private
   *
   * @param {JQuery} $modal
   */
  disableControlsOutsideCsrfStaleModal($modal) {
    const modalEl = $modal && $modal[0];

    if (!modalEl) {
      return;
    }

    const outsideModal = (_, el) => !modalEl.contains(el);

    $('button:visible, input[type="submit"]:visible, input[type="button"]:visible')
      .filter(outsideModal)
      .prop('disabled', true)
      .attr('aria-disabled', 'true');

    $('a.btn:visible')
      .filter(outsideModal)
      .addClass('disabled')
      .attr({ 'aria-disabled': 'true', tabindex: '-1' });
  }
};
